import { useState, type FormEvent } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useAction, useMutation, useQuery } from "convex/react";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ProjectGithub } from "@/components/github/project-github";
import { ProjectSlack } from "@/components/slack/project-slack";
import { ProjectAccess } from "@/components/settings/project-access";
import { AllowedOrigins } from "@/components/settings/allowed-origins";
import { ApiKeys } from "@/components/settings/api-keys";
import { ImageField } from "@/components/settings/image-field";
import { IssueTemplates } from "@/components/settings/issue-templates";
import { Labels } from "@/components/settings/labels";
import { PullRequestWorkflow } from "@/components/settings/pull-request-workflow";
import { SettingsSection } from "@/components/settings/section";
import { Webhooks } from "@/components/settings/webhooks";
import { EmptyState, ListSkeleton, QueryBoundary } from "@/components/shell/page";
import { useWorkspace, type ProjectSummary } from "@/components/shell/workspace-context";
import { Button } from "@/components/ui/button";
import { Field, FormError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { errorMessage } from "@/lib/i18n-errors";
import { NAME_MAX, cleanName } from "@/lib/slugs";

export const Route = createFileRoute(
  "/_app/$workspaceSlug/settings/projects/$projectSlug",
)({
  component: ProjectSettings,
});

function ProjectSettings() {
  const { t } = useTranslation("settings");
  const { t: tShell } = useTranslation("shell");
  const { workspaceSlug, projectSlug } = Route.useParams();
  const { projects } = useWorkspace();
  const project = projects?.find((item) => item.slug === projectSlug);

  return (
    <>
      <Link
        to="/$workspaceSlug/settings/projects"
        params={{ workspaceSlug }}
        className="-mb-6 flex w-fit items-center gap-1.5 rounded-md text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft aria-hidden strokeWidth={1.75} className="size-3.5" />
        {t("projects.title")}
      </Link>
      {projects === undefined ? (
        <ListSkeleton rows={2} className="rounded-lg border" />
      ) : project ? (
        // Keyed so the form starts from the right project when the URL changes.
        <>
          <GeneralForm key={project._id} project={project} />
          {/* Who can open it, and as what: anyone on it can see, an admin can change. */}
          <ProjectAccess key={`access:${project._id}`} project={project} />
          <Labels key={`labels:${project._id}`} projectId={project._id} role={project.role} />
          <IssueTemplates
            key={`templates:${project._id}`}
            projectId={project._id}
            role={project.role}
          />
          {/* Configuration: only an admin of the project can see or change it. */}
          {project.role === "admin" || project.role === "owner" ? (
            <QueryBoundary resetKey={project._id}>
              <ApiKeys projectId={project._id} />
              <AllowedOrigins projectId={project._id} />
              <Webhooks key={`webhooks:${project._id}`} projectId={project._id} />
            </QueryBoundary>
          ) : null}
          {/* The repositories it mirrors: an admin's to set, anyone's to see. */}
          <ProjectGithub key={`github:${project._id}`} project={project} />
          {/* The Slack channel its events are posted in. */}
          <ProjectSlack key={`slack:${project._id}`} project={project} />
          {/* What a pull request opened on one of them sets going. */}
          <PullRequestWorkflow
            key={`workflow:${project._id}`}
            projectId={project._id}
            role={project.role}
          />
        </>
      ) : (
        <EmptyState className="px-0 md:px-0" title={tShell("projectNotFound.body")} />
      )}
    </>
  );
}

function GeneralForm({ project }: { project: ProjectSummary }) {
  const { t } = useTranslation("settings");
  const { t: tErrors } = useTranslation("errors");
  const { t: tCommon } = useTranslation();
  const { workspace } = useWorkspace();
  const canEdit = project.role === "admin" || project.role === "owner";
  const [name, setName] = useState(project.name);
  const [error, setError] = useState<string | null>(null);
  const cleaned = cleanName(name);
  const dirty = cleaned !== project.name;

  // The new name is in the sidebar before the server has answered.
  const rename = useMutation(api.projects.rename).withOptimisticUpdate((store, args) => {
    const query = { workspaceId: workspace._id };
    const current = store.getQuery(api.projects.list, query);
    if (!current) return;
    store.setQuery(
      api.projects.list,
      query,
      current.map((item) =>
        item._id === args.projectId ? { ...item, name: args.name } : item,
      ),
    );
  });

  const generateImageUploadUrl = useMutation(api.projects.generateImageUploadUrl);
  const setImage = useMutation(api.projects.setImage);
  const findImages = useAction(api.repoImages.find);
  // Its repositories may hold its picture: offered once one is connected.
  const repos = useQuery(api.github.connect.listRepos, canEdit ? { projectId: project._id } : "skip");

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!cleaned) {
      setError(tErrors("nameInvalid"));
      return;
    }
    setError(null);
    try {
      await rename({ projectId: project._id, name: cleaned });
      toast(t("saved"));
    } catch (failure) {
      setError(errorMessage(failure, tErrors));
    }
  }

  return (
    <SettingsSection
      title={project.name}
      description={canEdit ? undefined : t("projects.readOnly")}
    >
      <form onSubmit={onSubmit} noValidate className="flex max-w-sm flex-col gap-4">
        <ImageField
          name={project.name}
          image={project.image}
          canEdit={canEdit}
          generateUploadUrl={() => generateImageUploadUrl({ projectId: project._id })}
          setImage={(storageId) => setImage({ projectId: project._id, storageId })}
          repository={
            repos?.length ? { find: () => findImages({ projectId: project._id }) } : undefined
          }
        />
        <Field label={t("projects.name")} htmlFor="project-name">
          <Input
            id="project-name"
            autoComplete="off"
            maxLength={NAME_MAX}
            value={name}
            disabled={!canEdit}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-3">
          <Field label={t("projects.slug")} htmlFor="project-slug">
            <Input id="project-slug" value={project.slug} readOnly disabled />
          </Field>
          <Field label={t("projects.key")} htmlFor="project-key">
            <Input id="project-key" value={project.key} readOnly disabled className="font-mono" />
          </Field>
        </div>
        <p className="type-label text-tertiary">{t("projects.fixedHint")}</p>
        <FormError>{error}</FormError>
        {canEdit ? (
          <div>
            <Button type="submit" disabled={!dirty}>
              {tCommon("actions.save")}
            </Button>
          </div>
        ) : null}
      </form>
    </SettingsSection>
  );
}
